import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import { uploadImage, getOtherBrands, createOtherBrand, updateOtherBrand, deleteOtherBrand } from '../utils/api';

// ── MESSAGE CARD COMPONENT ──
const MessageCard = ({ msg, onUpdate, statusColor, styles }) => {
  const [reply, setReply] = useState(msg.adminReply || '');
  const [showReply, setShowReply] = useState(false);

  return (
    <div style={styles.requestCard}>
      <div style={styles.requestHeader}>
        <div>
          <p style={styles.requestId}>#{msg._id.slice(-8).toUpperCase()}</p>
          <p style={styles.requestDate}>{new Date(msg.createdAt).toLocaleDateString()}</p>
        </div>
        <div style={styles.requestRight}>
          <span style={{ ...styles.statusBadge, background: `${statusColor(msg.status)}22`, color: statusColor(msg.status) }}>
            {msg.status}
          </span>
          <select
            style={styles.statusSelect}
            value={msg.status}
            onChange={e => onUpdate(msg._id, e.target.value, reply)}
          >
            {['unread', 'read', 'replied'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.requestBody}>
        <div style={styles.customerInfo}>
          <p style={styles.infoRow}><span style={styles.infoLabel}>👤 Name:</span> {msg.name}</p>
          <p style={styles.infoRow}><span style={styles.infoLabel}>📞 Phone:</span> {msg.phone || '—'}</p>
          <p style={styles.infoRow}><span style={styles.infoLabel}>📧 Email:</span> {msg.email}</p>
        </div>
        <div style={styles.orderItems}>
          <p style={styles.itemsTitle}>Message:</p>
          <p style={{ color: '#555', fontSize: '14px', lineHeight: 1.7 }}>{msg.message}</p>
        </div>
      </div>

      <div style={{ marginTop: '16px' }}>
        <button
          style={{ ...styles.editBtn, marginBottom: '8px' }}
          onClick={() => setShowReply(!showReply)}
        >
          {showReply ? 'Hide Reply' : '✏️ Write Reply'}
        </button>
        {showReply && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              style={{ ...styles.formInput, flex: 1 }}
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Type your reply..."
            />
            <button
              style={styles.submitBtn}
              onClick={() => onUpdate(msg._id, 'replied', reply)}
            >
              Send Reply
            </button>
          </div>
        )}
        {msg.adminReply && (
          <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', marginTop: '8px', border: '1px solid #bbf7d0' }}>
            <p style={{ color: '#15803d', fontSize: '13px', margin: 0 }}>✅ Reply sent: {msg.adminReply}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── ADMIN PAGE ──
const AdminPage = () => {
  const { user, logoutUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orderRequests, setOrderRequests] = useState([]);
  const [serviceBookings, setServiceBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [otherBrands, setOtherBrands] = useState([]);
  const [brandForm, setBrandForm] = useState({ brandName: '', description: '', rimSizes: [] });
  const [editingBrand, setEditingBrand] = useState(null);
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '', brand: '', type: 'tyre', category: 'Sedan',
    size: '', modelName: '', mspn: '', speedRating: '',
    price: '', discountPercent: 0,
    stock: '', description: '', isFeatured: false,
    images: [],
    compatibilityTags: []
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') return navigate('/admin-login');
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboard = async () => {
    try {
      const { data } = await API.get('/admin/dashboard');
      setStats(data.stats);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/products?limit=500');
      setProducts(data.products);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderRequests = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/order-requests');
      setOrderRequests(data.requests);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceBookings = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/service-bookings');
      setServiceBookings(data.bookings);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/contact');
      setMessages(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/admin/users');
      setUsers(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowProductForm(false);
    setEditingProduct(null);
    if (tab === 'products') fetchProducts();
    if (tab === 'orders') fetchOrderRequests();
    if (tab === 'services') fetchServiceBookings();
    if (tab === 'messages') fetchMessages();
    if (tab === 'users') fetchUsers();
    if (tab === 'dashboard') fetchDashboard();
    if (tab === 'brands') fetchOtherBrands();
  };

  const resetProductForm = () => {
  setProductForm({
    name: '', brand: '', type: 'tyre', category: 'Sedan',
    size: '', modelName: '', mspn: '', speedRating: '',
    price: '', discountPercent: 0,
    stock: '', description: '', isFeatured: false,
    images: [],
    compatibilityTags: []
  });
  setImageFile(null);
  setImagePreview('');
  setEditingProduct(null);
  setShowProductForm(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleImageUpload = async () => {
    if (!imageFile) return alert('Please select an image first');
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const { data } = await uploadImage(formData);
      setProductForm({ ...productForm, images: [data.url] });
      alert('Image uploaded successfully!');
    } catch (err) {
      alert('Image upload failed. Try again.');
    } finally {
      setImageUploading(false);
    }
  };

const handleProductSubmit = async (e) => {
  e.preventDefault();
  try {
    let finalImages = [...(productForm.images || [])];
    
    // Auto-upload image if they chose one but forgot to click upload
    if (imageFile && (!finalImages.length || imagePreview)) {
      // If we have an imageFile but we haven't uploaded it (or we changed it), upload now
      // Actually we just upload it if imageFile exists. To prevent double upload, we only do this
      // if finalImages doesn't already contain the URL of the current imageFile (which we can't easily check),
      // but imageFile is cleared after upload or reset.
      // Wait, in handleImageUpload we don't clear imageFile.
      // Let's just upload if imageFile is present and it hasn't been uploaded (we can check if imagePreview is a blob)
      if (imagePreview && imagePreview.startsWith('blob:')) {
        const formData = new FormData();
        formData.append('image', imageFile);
        const { data } = await uploadImage(formData);
        finalImages = [data.url];
      }
    }

    // Normalize all tags to proper object format no matter what
    const cleanTags = (productForm.compatibilityTags || [])
      .filter(tag => tag) // remove nulls
      .map(tag => {
        if (typeof tag === 'string') {
          const parts = tag.trim().split(/\s+/);
          return {
            car: parts[0] || '',
            model: parts.slice(1).join(' ') || '',
            years: [],
            suitableSizes: []
          };
        }
        return {
          car: String(tag.car || '').trim(),
          model: String(tag.model || '').trim(),
          years: Array.isArray(tag.years) ? tag.years : [],
          suitableSizes: Array.isArray(tag.suitableSizes) ? tag.suitableSizes : []
        };
      })
      .filter(tag => tag.car); // only keep tags that have at least a car brand

    const dataToSend = {
      name: productForm.name,
      brand: productForm.brand,
      type: productForm.type,
      category: productForm.category || 'Sedan',
      size: productForm.size,
      modelName: productForm.modelName || '',
      mspn: productForm.mspn || '',
      speedRating: productForm.speedRating || '',
      price: Number(productForm.price),
      discountPercent: Number(productForm.discountPercent) || 0,
      stock: Number(productForm.stock),
      description: productForm.description || '',
      isFeatured: productForm.isFeatured || false,
      images: finalImages,
      compatibilityTags: cleanTags
    };

    if (editingProduct) {
      await API.put(`/products/${editingProduct._id}`, dataToSend);
      alert('✅ Product updated successfully!');
    } else {
      await API.post('/products', dataToSend);
      alert('✅ Product added successfully!');
    }
    resetProductForm();
    fetchProducts();
  } catch (err) {
    console.error('Product save error:', err.response?.data);
    alert('Error: ' + (err.response?.data?.message || err.message));
  }
};

  const handleEditProduct = (product) => {
  setProductForm({
    name: product.name || '',
    brand: product.brand || '',
    type: product.type || 'tyre',
    category: product.category || 'Sedan',
    size: product.size || '',
    modelName: product.modelName || '',
    mspn: product.mspn || '',
    speedRating: product.speedRating || '',
    price: product.price || '',
    discountPercent: product.discountPercent || 0,
    stock: product.stock || '',
    description: product.description || '',
    isFeatured: product.isFeatured || false,
    images: product.images || [],
    compatibilityTags: product.compatibilityTags || []
  });
  setImagePreview('');
  setImageFile(null);
  setEditingProduct(product);
  setShowProductForm(true);
  window.scrollTo(0, 0);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await API.delete(`/products/${id}`);
      fetchProducts();
    } catch (err) {
      alert('Error deleting product');
    }
  };

  const handleUpdateOrderStatus = async (id, status) => {
    try {
      await API.put(`/order-requests/${id}/status`, { status });
      fetchOrderRequests();
      fetchDashboard();
    } catch (err) {
      alert('Error updating order status');
    }
  };

  const handleUpdateServiceStatus = async (id, status) => {
    try {
      await API.put(`/service-bookings/${id}/status`, { status });
      fetchServiceBookings();
      fetchDashboard();
    } catch (err) {
      alert('Error updating service status');
    }
  };

  const handleUpdateMessage = async (id, status, adminReply) => {
    try {
      await API.put(`/contact/${id}`, { status, adminReply });
      fetchMessages();
    } catch (err) {
      alert('Error updating message');
    }
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/admin-login');
  };

  const fetchOtherBrands = async () => {
    try {
      const { data } = await getOtherBrands();
      setOtherBrands(data);
    } catch (err) { console.log(err); }
  };

  const handleSaveBrand = async () => {
    if (!brandForm.brandName.trim()) return alert('Brand name is required');
    try {
      if (editingBrand) {
        await updateOtherBrand(editingBrand._id, brandForm);
      } else {
        await createOtherBrand(brandForm);
      }
      setBrandForm({ brandName: '', description: '', rimSizes: [] });
      setEditingBrand(null);
      setShowBrandForm(false);
      fetchOtherBrands();
    } catch (err) { alert('Error saving brand'); }
  };

  const handleDeleteBrand = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    try {
      await deleteOtherBrand(id);
      fetchOtherBrands();
    } catch (err) { alert('Error deleting brand'); }
  };

  const addRimSize = () => {
    setBrandForm({ ...brandForm, rimSizes: [...brandForm.rimSizes, { rim: '', sizes: [] }] });
  };

  const updateRim = (ri, val) => {
    const rs = [...brandForm.rimSizes];
    rs[ri] = { ...rs[ri], rim: val };
    setBrandForm({ ...brandForm, rimSizes: rs });
  };

  const addSizeToRim = (ri) => {
    const rs = [...brandForm.rimSizes];
    rs[ri].sizes = [...(rs[ri].sizes || []), { size: '', mspn: '', rating: '', notes: '' }];
    setBrandForm({ ...brandForm, rimSizes: rs });
  };

  const updateSizeEntry = (ri, si, field, val) => {
    const rs = [...brandForm.rimSizes];
    rs[ri].sizes[si] = { ...rs[ri].sizes[si], [field]: val };
    setBrandForm({ ...brandForm, rimSizes: rs });
  };

  const removeSizeEntry = (ri, si) => {
    const rs = [...brandForm.rimSizes];
    rs[ri].sizes = rs[ri].sizes.filter((_, i) => i !== si);
    setBrandForm({ ...brandForm, rimSizes: rs });
  };

  const removeRim = (ri) => {
    setBrandForm({ ...brandForm, rimSizes: brandForm.rimSizes.filter((_, i) => i !== ri) });
  };

  const statusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      contacted: '#2d5a7b',
      confirmed: '#c41e3a',
      completed: '#22c55e',
      cancelled: '#ef4444',
      unread: '#f59e0b',
      read: '#2d5a7b',
      replied: '#22c55e'
    };
    return colors[status] || '#aaa';
  };

  if (loading && !stats) return (
    <div style={styles.loadingPage}>
      <p>Loading dashboard...</p>
    </div>
  );

  return (
    <div style={styles.layout}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarTop}>
          <h2 style={styles.sidebarLogo}>Empire1Tires Admin Portal</h2>
          <p style={styles.sidebarRole}>Owner Panel</p>
        </div>

        <nav style={styles.sidebarNav}>
          {[
            { id: 'dashboard', icon: '📊', label: 'Dashboard' },
            { id: 'products', icon: '📦', label: 'Products' },
            { id: 'orders', icon: '🛒', label: 'Order Requests' },
            { id: 'services', icon: '🔧', label: 'Service Bookings' },
            { id: 'messages', icon: '💬', label: 'Messages' },
            { id: 'users', icon: '👥', label: 'Customers' },
            { id: 'brands', icon: '🏷️', label: 'Other Brands' },
          ].map(tab => (
            <button
              key={tab.id}
              style={{
                ...styles.sidebarBtn,
                background: activeTab === tab.id ? '#c41e3a' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#d0dce8'
              }}
              onClick={() => handleTabChange(tab.id)}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div style={styles.sidebarBottom}>
          <button style={styles.visitBtn} onClick={() => navigate('/')}>
            🌐 View Website
          </button>
          <button style={styles.logoutBtn} onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.main}>

        {/* ── DASHBOARD ── */}
        {activeTab === 'dashboard' && stats && (
          <div>
            {/* Header */}
            <div style={styles.dashHeader}>
              <div>
                <h1 style={styles.dashTitle}>Dashboard</h1>
                <p style={styles.dashSubtitle}>Welcome back, <strong>{user?.name}</strong> 👋 Here's what's happening today.</p>
              </div>
              <div style={styles.dashDate}>
                📅 {new Date().toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>

            {/* Stats Grid - UNIFIED NAVY/BURGUNDY THEME */}
            <div style={styles.statsGrid}>
              {[
                { label: 'Total Products', value: stats.totalProducts, icon: '📦' },
                { label: 'Total Orders', value: stats.totalOrders, icon: '🛒' },
                { label: 'Customers', value: stats.totalUsers, icon: '👥' },
                { label: 'Pending Orders', value: stats.pendingOrders, icon: '⏳' },
                { label: 'Pending Services', value: stats.pendingServiceBookings, icon: '🔧' },
                { label: 'Unread Messages', value: stats.unreadMessages, icon: '💬' },
                { label: 'Total Revenue', value: `$ ${stats.totalRevenue?.toLocaleString()}`, icon: '💰' },
              ].map((stat, i) => (
                <div key={i} style={styles.statCard}>
                  <div style={styles.statTop}>
                    <div style={styles.statIconBox}>
                      <span style={styles.statIcon}>{stat.icon}</span>
                    </div>
                    <p style={styles.statLabel}>
                      {stat.label}
                      {stat.label === 'Total Revenue' && <span style={{display: 'block', fontSize: '9px', color: '#888', marginTop: '2px'}}>(From Orders Only)</span>}
                    </p>
                  </div>
                  <p style={styles.statValue}>{stat.value}</p>
                  <div style={styles.statBar}>
                    <div style={styles.statBarFill} />
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <h2 style={styles.sectionHeading}>Quick Actions</h2>
            <div style={styles.quickActions}>
              {[
                { label: 'Add Product', icon: '➕', tab: 'products' },
                { label: 'View Orders', icon: '🛒', tab: 'orders' },
                { label: 'View Bookings', icon: '🔧', tab: 'services' },
                { label: 'Read Messages', icon: '💬', tab: 'messages' },
              ].map((action, i) => (
                <button
                  key={i}
                  style={styles.quickActionBtn}
                  onClick={() => handleTabChange(action.tab)}
                >
                  <span style={styles.quickActionIcon}>{action.icon}</span>
                  <span style={styles.quickActionLabel}>{action.label}</span>
                </button>
              ))}
            </div>

            {/* Info Cards Row */}
            <div style={styles.infoRow}>
              {/* Store Info */}
              <div style={styles.infoCard}>
                <h3 style={styles.infoCardTitle}>🏪 Store Overview</h3>
                <div style={styles.infoList}>
                  <div style={styles.infoListItem}>
                    <span style={styles.infoListLabel}>Total Products</span>
                    <span style={styles.infoListValue}>{stats.totalProducts}</span>
                  </div>
                  <div style={styles.infoListItem}>
                    <span style={styles.infoListLabel}>Total Orders</span>
                    <span style={styles.infoListValue}>{stats.totalOrders}</span>
                  </div>
                  <div style={styles.infoListItem}>
                    <span style={styles.infoListLabel}>Total Customers</span>
                    <span style={styles.infoListValue}>{stats.totalUsers}</span>
                  </div>
                  <div style={styles.infoListItem}>
                    <span style={styles.infoListLabel}>Total Revenue</span>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ ...styles.infoListValue, color: '#c41e3a', fontWeight: 'bold' }}>
                        $ {stats.totalRevenue?.toLocaleString()}
                      </span>
                      <span style={{ display: 'block', fontSize: '10px', color: '#888' }}>(Orders Only)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Actions */}
              <div style={styles.infoCard}>
                <h3 style={styles.infoCardTitle}>⚡ Needs Attention</h3>
                <div style={styles.infoList}>
                  {[
                    { label: 'Pending Orders', value: stats.pendingOrders },
                    { label: 'Pending Services', value: stats.pendingServiceBookings },
                    { label: 'Unread Messages', value: stats.unreadMessages },
                  ].map((item, i) => (
                    <div key={i} style={styles.infoListItem}>
                      <span style={styles.infoListLabel}>{item.label}</span>
                      <span style={styles.alertBadge}>
                        {item.value} pending
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Store Status */}
              <div style={{ ...styles.infoCard, background: 'linear-gradient(135deg, #1a3a5c, #2d5a7b)' }}>
                <h3 style={{ ...styles.infoCardTitle, color: '#fff' }}>🔴 Empire1Tires Admin Portal</h3>
                <p style={styles.storeStatusText}>Your store is live and running!</p>
                <div style={styles.storeStatusDot}>
                  <div style={styles.statusDotGreen} />
                  <span style={styles.statusDotLabel}>All systems operational</span>
                </div>
                <button
                  style={styles.viewSiteBtn}
                  onClick={() => window.open('/', '_blank')}
                >
                  🌐 View Live Site →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── PRODUCTS ── */}
        {activeTab === 'products' && (
          <div>
            <div style={styles.pageHeader}>
              <div>
                <h1 style={styles.pageTitle}>Products</h1>
                <p style={styles.pageSubtitle}>{products.length} total products</p>
              </div>
              <button
                style={styles.addBtn}
                onClick={() => { resetProductForm(); setShowProductForm(!showProductForm); }}
              >
                {showProductForm ? '✕ Cancel' : '+ Add Product'}
              </button>
            </div>

        {showProductForm && (
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>
                {editingProduct ? '✏️ Edit Product' : '➕ Add New Product'}
              </h3>
              <form onSubmit={handleProductSubmit}>
                <div style={styles.formGrid}>

                  {/* ── Product Name ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Product Name *</label>
                    <input style={styles.formInput} value={productForm.name}
                      onChange={e => setProductForm({ ...productForm, name: e.target.value })}
                      placeholder="e.g. Primacy 4, Grand N704 H/T" required />
                  </div>

                  {/* ── Brand ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Brand *</label>
                    <input style={styles.formInput} list="brand-list" value={productForm.brand}
                      onChange={e => setProductForm({ ...productForm, brand: e.target.value })}
                      placeholder="Select or type brand" required />
                    <datalist id="brand-list">
                      {['Michelin','Bridgestone','Continental','Goodyear','Pirelli','Dunlop','Falken','Nexen','Hankook','Toyo','Cooper','BFGoodrich','Kumho','Yokohama','Nitto','Nebula','General'].map(b => (
                        <option key={b} value={b} />
                      ))}
                    </datalist>
                  </div>

                  {/* ── Type ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Type *</label>
                    <select style={styles.formInput} value={productForm.type}
                      onChange={e => setProductForm({ ...productForm, type: e.target.value })}>
                      <option value="tyre">Tire</option>
                      <option value="rim">Rim</option>
                    </select>
                  </div>

                  {/* ── Category (Rim Size or Tire Type) ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Category *</label>
                    <select style={styles.formInput} value={productForm.category}
                      onChange={e => setProductForm({ ...productForm, category: e.target.value })}>
                      <optgroup label="── By Rim Size ──">
                        {['R16','R17','R18','R19','R20','R21','R22','R23','R24'].map(r => <option key={r} value={r}>{r}</option>)}
                      </optgroup>
                      <optgroup label="── Tire Type ──">
                        <option value="Sedan">Sedan</option>
                        <option value="SUV">SUV</option>
                        <option value="Hatchback">Hatchback</option>
                        <option value="Off-Road">Off-Road</option>
                        <option value="Performance">Performance</option>
                        <option value="Electric Vehicle">Electric Vehicle</option>
                      </optgroup>
                      <optgroup label="── Rim Type ──">
                        <option value="Alloy Rim">Alloy Rim</option>
                        <option value="Chrome Rim">Chrome Rim</option>
                        <option value="Matte Black Rim">Matte Black Rim</option>
                        <option value="Luxury Rim">Luxury Rim</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* ── Tire Size ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Tire / Rim Size * <span style={styles.optionalTag}>(e.g. 225/55R17)</span></label>
                    <input style={styles.formInput} list="size-list" value={productForm.size}
                      onChange={e => setProductForm({ ...productForm, size: e.target.value })}
                      placeholder="e.g. 225/55R17 or type custom" required />
                    <datalist id="size-list">
                      {['205/55R16','205/60R16','215/55R16','215/60R16',
                        '205/50R17','205/55R17','215/45R17','215/50R17','215/55R17','225/45R17','225/50R17','245/45ZR17',
                        '225/40R18','225/45R18','225/50R18','235/40R18','235/45R18','245/40R18','255/35R18','265/60R18',
                        '225/40R19','235/40R19','245/45R19','255/35R19','255/45R19','265/50R19',
                        '235/45R20','245/40R20','255/40R20','255/45R20','265/45R20',
                        '245/45R21','255/40R21','275/40R21','285/45R21',
                        '255/45R22','275/40R22','285/40R22','285/45R22',
                        '265/75R16','225/65R17','265/70R17','275/55R20','285/50R20'
                       ].map(s => <option key={s} value={s} />)}
                    </datalist>
                  </div>

                  {/* ── MSPN ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>MSPN # <span style={styles.optionalTag}>(Michelin code, optional)</span></label>
                    <input style={styles.formInput} value={productForm.mspn}
                      onChange={e => setProductForm({ ...productForm, mspn: e.target.value })}
                      placeholder="e.g. 35861" />
                  </div>

                  {/* ── Speed Rating ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Speed Rating <span style={styles.optionalTag}>(optional)</span></label>
                    <input style={styles.formInput} list="rating-list" value={productForm.speedRating}
                      onChange={e => setProductForm({ ...productForm, speedRating: e.target.value })}
                      placeholder="e.g. 95V, 98H, (99Y)" />
                    <datalist id="rating-list">
                      {['91H','91V','92V','93V','94H','94V','95H','95V','95W','96V','97H','97V','98H','98V','99V','99W','100V','101V','101W','102V','104V','105V','107V','108V','110V','113V','114H','(99Y)','(103Y)'].map(r => <option key={r} value={r} />)}
                    </datalist>
                  </div>

                  {/* ── Price ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Price (USD) *</label>
                    <div style={styles.inputWithPrefix}>
                      <span style={styles.inputPrefix}>$</span>
                      <input style={{ ...styles.formInput, paddingLeft: '28px' }} type="number"
                        value={productForm.price}
                        onChange={e => setProductForm({ ...productForm, price: e.target.value })}
                        placeholder="e.g. 180" required />
                    </div>
                  </div>

                  {/* ── Discount ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Discount % <span style={styles.optionalTag}>(0 = no discount)</span></label>
                    <input style={styles.formInput} type="number" min="0" max="100"
                      value={productForm.discountPercent}
                      onChange={e => setProductForm({ ...productForm, discountPercent: e.target.value })}
                      placeholder="0" />
                  </div>

                  {/* ── Stock ── */}
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Stock Quantity *</label>
                    <input style={styles.formInput} type="number"
                      value={productForm.stock}
                      onChange={e => setProductForm({ ...productForm, stock: e.target.value })}
                      placeholder="e.g. 20" required />
                  </div>

                  {/* ── Description ── */}
                  <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
                    <label style={styles.formLabel}>Description <span style={styles.optionalTag}>(optional)</span></label>
                    <textarea style={{ ...styles.formInput, height: '80px', resize: 'vertical' }}
                      value={productForm.description}
                      onChange={e => setProductForm({ ...productForm, description: e.target.value })}
                      placeholder="Brief product description (features, benefits, etc.)" />
                  </div>

                  {/* ── Image Upload ── */}
                  <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
                    <label style={styles.formLabel}>Product Image <span style={styles.optionalTag}>(optional)</span></label>
                    <div style={styles.imageUploadBox}>
                      {imagePreview || productForm.images[0] ? (
                        <div style={styles.imagePreviewBox}>
                          <img src={imagePreview || productForm.images[0]} alt="preview" style={styles.imagePreview} />
                          <button type="button" style={styles.removeImageBtn}
                            onClick={() => { setImagePreview(''); setImageFile(null); setProductForm({ ...productForm, images: [] }); }}>
                            ✕ Remove
                          </button>
                        </div>
                      ) : (
                        <div style={styles.imagePlaceholder}>
                          <span style={{ fontSize: '32px' }}>📷</span>
                          <p style={{ color: '#aaa', fontSize: '13px', margin: '8px 0 0' }}>No image selected</p>
                        </div>
                      )}
                      <div style={styles.uploadControls}>
                        <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="imageInput" />
                        <label htmlFor="imageInput" style={styles.chooseFileBtn}>📁 Choose Image</label>
                        <button type="button" style={{ ...styles.uploadBtn, opacity: imageUploading ? 0.7 : 1 }}
                          onClick={handleImageUpload} disabled={imageUploading || !imageFile}>
                          {imageUploading ? 'Uploading...' : '☁️ Upload to Cloud'}
                        </button>
                      </div>
                      {productForm.images[0] && <p style={styles.uploadedNote}>✅ Image uploaded successfully</p>}
                    </div>
                  </div>

                  {/* ── Featured ── */}
                  <div style={styles.formField}>
                    <label style={styles.checkLabel}>
                      <input type="checkbox" checked={productForm.isFeatured}
                        onChange={e => setProductForm({ ...productForm, isFeatured: e.target.checked })} />
                      &nbsp; ⭐ Mark as Featured (shows in carousel on homepage)
                    </label>
                  </div>

                </div>

                <div style={styles.formActions}>
                  <button type="submit" style={styles.submitBtn}>
                    {editingProduct ? '✅ Update Product' : '✅ Add Product'}
                  </button>
                  <button type="button" style={styles.cancelBtn} onClick={resetProductForm}>Cancel</button>
                </div>
              </form>
            </div>
            )}








            <div style={styles.tableBox}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Product', 'Brand', 'Type', 'Size', 'MSPN', 'Speed', 'Price', 'Discount', 'Stock', 'Featured', 'Actions'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>

                {products.map(p => (
                      <tr key={p._id} style={styles.tr}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={styles.td}>
                          <div style={styles.productNameCell}>
                            {p.images?.[0] && (
                              <img src={p.images[0]} alt={p.name} style={styles.productThumb} />
                            )}
                            <span>{p.name}</span>
                          </div>
                        </td>
                        <td style={styles.td}>{p.brand}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.typeBadge,
                            background: p.type === 'tyre' ? '#eff6ff' : '#fdf4ff',
                            color: p.type === 'tyre' ? '#3b82f6' : '#c41e3a'
                          }}>
                            {p.type === 'tyre' ? 'Tire' : 'Rim'}
                          </span>
                        </td>
                        <td style={styles.td}>{p.size}</td>
                        <td style={styles.td}>{p.mspn ? <span style={{ color: '#c41e3a', fontWeight: '600', fontSize: '12px' }}>#{p.mspn}</span> : <span style={{ color: '#aaa' }}>—</span>}</td>
                        <td style={styles.td}>{p.speedRating || <span style={{ color: '#aaa' }}>—</span>}</td>
                        <td style={styles.td}>${p.price?.toLocaleString()}</td>
                        <td style={styles.td}>
                          {p.discountPercent > 0
                            ? <span style={styles.discountTag}>{p.discountPercent}% OFF</span>
                            : <span style={{ color: '#aaa' }}>—</span>
                          }
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: p.stock < 5 ? '#ef4444' : p.stock < 10 ? '#f59e0b' : '#22c55e', fontWeight: 'bold' }}>
                            {p.stock}
                          </span>
                        </td>
                        <td style={styles.td}>{p.isFeatured ? '⭐' : '—'}</td>
                        <td style={styles.td}>

                          <div style={styles.actionBtns}>
                            <button style={styles.editBtn} onClick={() => handleEditProduct(p)}>Edit</button>
                            <button style={styles.deleteBtn} onClick={() => handleDeleteProduct(p._id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ORDER REQUESTS ── */}
        {activeTab === 'orders' && (
          <div>
            <h1 style={styles.pageTitle}>Order Requests</h1>
            <p style={styles.pageSubtitle}>{orderRequests.length} total requests</p>

            {orderRequests.length === 0 ? (
              <div style={styles.emptyBox}>
                <p style={styles.emptyIcon}>🛒</p>
                <p style={styles.emptyText}>No order requests yet</p>
              </div>
            ) : (
              orderRequests.map(req => (
                <div key={req._id} style={styles.requestCard}>
                  <div style={styles.requestHeader}>
                    <div>
                      <p style={styles.requestId}>#{req._id.slice(-8).toUpperCase()}</p>
                      <p style={styles.requestDate}>{new Date(req.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div style={styles.requestRight}>
                      <span style={{ ...styles.statusBadge, background: `${statusColor(req.status)}22`, color: statusColor(req.status) }}>
                        {req.status}
                      </span>
                      <select
                        style={styles.statusSelect}
                        value={req.status}
                        onChange={e => handleUpdateOrderStatus(req._id, e.target.value)}
                      >
                        {['pending', 'contacted', 'confirmed', 'cancelled'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={styles.requestBody}>
                    <div style={styles.customerInfo}>
                      <p style={styles.infoRow}><span style={styles.infoLabel}>👤 Name:</span> {req.customerName}</p>
                      <p style={styles.infoRow}><span style={styles.infoLabel}>📞 Phone:</span> {req.phone}</p>
                      {req.email && <p style={styles.infoRow}><span style={styles.infoLabel}>📧 Email:</span> {req.email}</p>}
                      <p style={styles.infoRow}><span style={styles.infoLabel}>📍 Address:</span> {req.address}, {req.city}</p>
                      {req.message && <p style={styles.infoRow}><span style={styles.infoLabel}>💬 Note:</span> {req.message}</p>}
                    </div>
                    <div style={styles.orderItems}>
                      <p style={styles.itemsTitle}>Ordered Items:</p>
                      {req.items.map((item, i) => (
                        <div key={i} style={styles.orderItem}>
                          <span>{item.brand} {item.name} — {item.size}</span>
                          <span>x{item.quantity} — $ {(item.price * item.quantity).toLocaleString()}</span>
                        </div>
                      ))}
                      <div style={styles.orderTotal}>
                        <span>Total:</span>
                        <span style={{ color: '#c41e3a', fontWeight: 'bold' }}>$ {req.subtotal?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── SERVICE BOOKINGS ── */}
        {activeTab === 'services' && (
          <div>
            <h1 style={styles.pageTitle}>Service Bookings</h1>
            <p style={styles.pageSubtitle}>{serviceBookings.length} total bookings</p>

            {serviceBookings.length === 0 ? (
              <div style={styles.emptyBox}>
                <p style={styles.emptyIcon}>🔧</p>
                <p style={styles.emptyText}>No service bookings yet</p>
              </div>
            ) : (
              serviceBookings.map(booking => (
                <div key={booking._id} style={styles.requestCard}>
                  <div style={styles.requestHeader}>
                    <div>
                      <p style={styles.requestId}>#{booking._id.slice(-8).toUpperCase()}</p>
                      <p style={styles.requestDate}>{new Date(booking.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div style={styles.requestRight}>
                      <span style={{ ...styles.statusBadge, background: `${statusColor(booking.status)}22`, color: statusColor(booking.status) }}>
                        {booking.status}
                      </span>
                      <select
                        style={styles.statusSelect}
                        value={booking.status}
                        onChange={e => handleUpdateServiceStatus(booking._id, e.target.value)}
                      >
                        {['pending', 'confirmed', 'completed', 'cancelled'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={styles.requestBody}>
                    <div style={styles.customerInfo}>
                      <p style={styles.infoRow}><span style={styles.infoLabel}>👤 Name:</span> {booking.customerName}</p>
                      <p style={styles.infoRow}><span style={styles.infoLabel}>📞 Phone:</span> {booking.phone}</p>
                      {booking.email && <p style={styles.infoRow}><span style={styles.infoLabel}>📧 Email:</span> {booking.email}</p>}
                      <p style={styles.infoRow}><span style={styles.infoLabel}>🚗 Vehicle:</span> {booking.vehicleBrand} {booking.vehicleModel} {booking.vehicleYear}</p>
                      {booking.preferredDate && <p style={styles.infoRow}><span style={styles.infoLabel}>📅 Date:</span> {booking.preferredDate}</p>}
                      {booking.preferredTime && <p style={styles.infoRow}><span style={styles.infoLabel}>🕐 Time:</span> {booking.preferredTime}</p>}
                      {booking.message && <p style={styles.infoRow}><span style={styles.infoLabel}>💬 Note:</span> {booking.message}</p>}
                    </div>
                    <div style={styles.orderItems}>
                      <p style={styles.itemsTitle}>Services Requested:</p>
                      <div style={styles.orderItem}>
                        <span>✅ Main: <strong>{booking.service}</strong></span>
                      </div>
                      {booking.additionalServices?.length > 0 && booking.additionalServices.map((s, i) => (
                        <div key={i} style={styles.orderItem}>
                          <span>➕ {s}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── MESSAGES ── */}
        {activeTab === 'messages' && (
          <div>
            <h1 style={styles.pageTitle}>Customer Messages</h1>
            <p style={styles.pageSubtitle}>{messages.length} total messages</p>

            {messages.length === 0 ? (
              <div style={styles.emptyBox}>
                <p style={styles.emptyIcon}>💬</p>
                <p style={styles.emptyText}>No messages yet</p>
              </div>
            ) : (
              messages.map(msg => (
                <MessageCard
                  key={msg._id}
                  msg={msg}
                  onUpdate={handleUpdateMessage}
                  statusColor={statusColor}
                  styles={styles}
                />
              ))
            )}
          </div>
        )}

        {/* ── CUSTOMERS ── */}
        {activeTab === 'users' && (
          <div>
            <h1 style={styles.pageTitle}>Customers</h1>
            <p style={styles.pageSubtitle}>{users.filter(u => u.role !== 'admin').length} registered customers</p>

            {users.filter(u => u.role !== 'admin').length === 0 ? (
              <div style={styles.emptyBox}>
                <p style={styles.emptyIcon}>👥</p>
                <p style={styles.emptyText}>No registered customers yet</p>
              </div>
            ) : (
              <div style={styles.tableBox}>
                <table style={styles.table}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['#', 'Name', 'Email', 'Phone', 'Role', 'Joined', 'Actions'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.role !== 'admin').map((u, i) => (
                      <tr key={u._id} style={styles.tr}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ ...styles.td, color: '#aaa' }}>{i + 1}</td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#1a3a5c', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>
                              {u.name?.charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontWeight: '500', color: '#1a3a5c' }}>{u.name}</span>
                          </div>
                        </td>
                        <td style={styles.td}>{u.email || <span style={{ color: '#aaa' }}>—</span>}</td>
                        <td style={styles.td}>{u.phone || <span style={{ color: '#aaa' }}>—</span>}</td>
                        <td style={styles.td}>
                          <span style={{ background: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>Customer</span>
                        </td>
                        <td style={styles.td}>{new Date(u.createdAt).toLocaleDateString()}</td>
                        <td style={styles.td}>
                          <button style={styles.deleteBtn} onClick={async () => {
                            if (window.confirm(`Delete customer ${u.name}?`)) {
                              await API.delete(`/admin/users/${u._id}`);
                              fetchUsers();
                            }
                          }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {/* ── OTHER BRANDS ── */}
        {activeTab === 'brands' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h1 style={styles.pageTitle}>Other Brands</h1>
                <p style={styles.pageSubtitle}>Add tire brands with rim sizes shown in Tire Finder for customers</p>
              </div>
              <button style={styles.addBtn}
                onClick={() => { setBrandForm({ brandName: '', description: '', rimSizes: [] }); setEditingBrand(null); setShowBrandForm(!showBrandForm); }}>
                {showBrandForm ? '✕ Cancel' : '+ Add Brand'}
              </button>
            </div>

            {showBrandForm && (
              <div style={styles.formCard}>
                <h3 style={styles.formTitle}>{editingBrand ? '✏️ Edit Brand' : '➕ Add Brand'}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={styles.formLabel}>Brand Name *</label>
                    <input style={styles.formInput} value={brandForm.brandName}
                      onChange={e => setBrandForm({ ...brandForm, brandName: e.target.value })}
                      placeholder="e.g. Bridgestone, Continental" />
                  </div>
                  <div>
                    <label style={styles.formLabel}>Description (optional)</label>
                    <input style={styles.formInput} value={brandForm.description}
                      onChange={e => setBrandForm({ ...brandForm, description: e.target.value })}
                      placeholder="Short description" />
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <label style={styles.formLabel}>Rim Sizes</label>
                      <button type="button" style={{ ...styles.editBtn, padding: '6px 14px' }} onClick={addRimSize}>+ Add Rim Size</button>
                    </div>
                    {brandForm.rimSizes.map((rs, ri) => (
                      <div key={ri} style={{ background: '#f5f7fa', borderRadius: '10px', padding: '14px', marginBottom: '10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                          <label style={{ ...styles.formLabel, margin: 0 }}>R</label>
                          <input style={{ ...styles.formInput, width: '80px' }} type="number"
                            value={rs.rim} onChange={e => updateRim(ri, e.target.value)} placeholder="18" />
                          <button type="button" style={{ ...styles.deleteBtn, marginLeft: 'auto' }} onClick={() => removeRim(ri)}>✕ Remove Rim</button>
                        </div>
                        {(rs.sizes || []).map((s, si) => (
                          <div key={si} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                            <input style={styles.formInput} value={s.size} onChange={e => updateSizeEntry(ri, si, 'size', e.target.value)} placeholder="225/55R18" />
                            <input style={styles.formInput} value={s.rating} onChange={e => updateSizeEntry(ri, si, 'rating', e.target.value)} placeholder="98V" />
                            <input style={styles.formInput} value={s.mspn} onChange={e => updateSizeEntry(ri, si, 'mspn', e.target.value)} placeholder="MSPN" />
                            <input style={styles.formInput} value={s.notes} onChange={e => updateSizeEntry(ri, si, 'notes', e.target.value)} placeholder="Notes" />
                            <button type="button" style={styles.deleteBtn} onClick={() => removeSizeEntry(ri, si)}>✕</button>
                          </div>
                        ))}
                        <button type="button" style={{ ...styles.editBtn, padding: '6px 12px', fontSize: '11px' }} onClick={() => addSizeToRim(ri)}>+ Add Tire Size</button>
                      </div>
                    ))}
                  </div>
                  <div style={styles.formActions}>
                    <button style={styles.submitBtn} onClick={handleSaveBrand}>{editingBrand ? '✅ Update' : '✅ Save Brand'}</button>
                    <button style={styles.cancelBtn} onClick={() => { setShowBrandForm(false); setEditingBrand(null); }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {otherBrands.length === 0 ? (
              <div style={styles.emptyBox}>
                <p style={styles.emptyIcon}>🏷️</p>
                <p style={styles.emptyText}>No other brands added yet.</p>
              </div>
            ) : (
              otherBrands.map(brand => (
                <div key={brand._id} style={styles.requestCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ ...styles.requestId, fontSize: '17px' }}>{brand.brandName}</p>
                      {brand.description && <p style={{ color: '#4a5568', fontSize: '13px', margin: '4px 0 0' }}>{brand.description}</p>}
                      <p style={{ color: '#8a9bae', fontSize: '12px', margin: '4px 0 0' }}>{brand.rimSizes?.length || 0} rim sizes</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={styles.editBtn} onClick={() => {
                        setBrandForm({ brandName: brand.brandName, description: brand.description, rimSizes: brand.rimSizes });
                        setEditingBrand(brand); setShowBrandForm(true); setExpandedBrand(null);
                      }}>Edit</button>
                      <button style={styles.deleteBtn} onClick={() => handleDeleteBrand(brand._id)}>Delete</button>
                      <button style={{ ...styles.editBtn, background: '#f0f3f8' }}
                        onClick={() => setExpandedBrand(expandedBrand === brand._id ? null : brand._id)}>
                        {expandedBrand === brand._id ? '▲ Hide' : '▼ View Sizes'}
                      </button>
                    </div>
                  </div>
                  {expandedBrand === brand._id && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                      {(brand.rimSizes || []).map((rs, ri) => (
                        <div key={ri} style={{ marginBottom: '12px' }}>
                          <p style={{ color: '#1a3a5c', fontWeight: '700', marginBottom: '6px' }}>R{rs.rim}</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {(rs.sizes || []).map((s, si) => (
                              <div key={si} style={{ background: '#f5f7fa', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 12px', fontSize: '13px' }}>
                                <strong>{s.size}</strong>{s.rating && ` · ${s.rating}`}
                                {s.mspn && <span style={{ color: '#c41e3a', marginLeft: '6px' }}>#{s.mspn}</span>}
                                {s.notes && <p style={{ color: '#8a9bae', fontSize: '11px', margin: '2px 0 0' }}>{s.notes}</p>}
                              </div>
                            ))}
                            {!rs.sizes?.length && <p style={{ color: '#aaa', fontSize: '12px' }}>No sizes</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  layout: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#f5f7fa' },
  loadingPage: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7fa', color: '#666' },
  sidebar: { width: '240px', background: '#1a3a5c', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarTop: { padding: '28px 20px 20px' },
  sidebarLogo: { color: '#c41e3a', fontSize: '20px', fontWeight: 'bold', marginBottom: '4px' },
  sidebarRole: { color: '#8fafc4', fontSize: '12px' },
  sidebarNav: { flex: 1, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' },
  sidebarBtn: { display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'background 0.2s', textAlign: 'left' },
  sidebarBottom: { padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #2a4a6e', marginTop: 'auto' },
  visitBtn: { padding: '10px', background: 'transparent', color: '#d0dce8', border: '1px solid #2a4a6e', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  logoutBtn: { padding: '10px', background: 'rgba(196, 30, 58, 0.2)', color: '#ff8896', border: '1px solid rgba(196,30,58,0.3)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  main: { flex: 1, padding: '40px', overflowY: 'auto', background: '#f5f7fa' },
  pageTitle: { color: '#1a3a5c', fontSize: '28px', fontWeight: 'bold', marginBottom: '4px' },
  pageSubtitle: { color: '#4a5568', fontSize: '14px', marginBottom: '28px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' },
  statCard: { background: '#ffffff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(26, 58, 92, 0.06)', border: '1px solid #e2e8f0' },
  statTop: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' },
  statIconBox: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: '#f0f3f8', border: '1px solid #d4a5ab' },
  statIcon: { fontSize: '20px' },
  statLabel: { color: '#4a5568', fontSize: '13px', margin: 0 },
  statValue: { fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#1a3a5c' },
  statBar: { height: '4px', borderRadius: '2px', marginTop: '16px', overflow: 'hidden', background: '#e2e8f0' },
  statBarFill: { height: '100%', borderRadius: '2px', background: '#c41e3a', width: '60%' },
  tableBox: { background: '#fff', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginTop: '20px' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '700px' },
  th: { color: '#4a5568', padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f8f9fa' },
  td: { color: '#444', padding: '14px 16px', fontSize: '13px', whiteSpace: 'nowrap' },
  statusBadge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  typeBadge: { padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  discountTag: { background: '#fff5f5', color: '#c41e3a', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' },
  actionBtns: { display: 'flex', gap: '6px' },
  editBtn: { padding: '5px 12px', background: '#eff6ff', color: '#3b82f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  deleteBtn: { padding: '5px 12px', background: '#fff5f5', color: '#c41e3a', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  statusSelect: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#f5f7fa', color: '#333', fontSize: '12px', cursor: 'pointer', marginLeft: '8px' },
  addBtn: { padding: '10px 24px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', marginBottom: '20px' },
  formCard: { background: '#fff', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px', border: '1px solid #e2e8f0' },
  formTitle: { color: '#1a3a5c', fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' },
  formField: { display: 'flex', flexDirection: 'column' },
  formLabel: { color: '#2d5a7b', fontSize: '13px', fontWeight: '600', marginBottom: '6px' },
  formInput: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f5f7fa', color: '#333', fontSize: '14px', outline: 'none', boxSizing: 'border-box', width: '100%' },
  checkLabel: { color: '#555', fontSize: '14px', display: 'flex', alignItems: 'center', marginTop: '24px', cursor: 'pointer' },
  formActions: { display: 'flex', gap: '12px', marginTop: '20px' },
  submitBtn: { padding: '11px 28px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  cancelBtn: { padding: '11px 24px', background: '#f5f7fa', color: '#666', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  requestCard: { background: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' },
  requestHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' },
  requestId: { color: '#1a3a5c', fontWeight: 'bold', fontSize: '15px', margin: 0 },
  requestDate: { color: '#aaa', fontSize: '12px', margin: '4px 0 0' },
  requestRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  requestBody: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' },
  customerInfo: {},
  //infoRow: { color: '#555', fontSize: '14px', marginBottom: '8px' },
  infoLabel: { fontWeight: '600', color: '#333' },
  orderItems: { background: '#f5f7fa', borderRadius: '10px', padding: '16px' },
  itemsTitle: { color: '#1a3a5c', fontWeight: 'bold', fontSize: '14px', marginBottom: '10px' },
  orderItem: { display: 'flex', justifyContent: 'space-between', color: '#555', fontSize: '13px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0' },
  orderTotal: { display: 'flex', justifyContent: 'space-between', color: '#333', fontSize: '14px', fontWeight: 'bold', marginTop: '8px' },
  emptyBox: { textAlign: 'center', padding: '80px 0' },
  emptyIcon: { fontSize: '48px', margin: '0 0 16px' },
  emptyText: { color: '#4a5568', fontSize: '16px' },
  imageUploadBox: { border: '2px dashed #e2e8f0', borderRadius: '12px', padding: '20px', background: '#f5f7fa' },
  imagePreviewBox: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' },
  imagePreview: { width: '100px', height: '100px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #e2e8f0' },
  removeImageBtn: { padding: '6px 12px', background: '#fff5f5', color: '#c41e3a', border: '1px solid #ffd0d3', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  imagePlaceholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100px', marginBottom: '16px' },
  uploadControls: { display: 'flex', gap: '12px', alignItems: 'center' },
  chooseFileBtn: { padding: '9px 18px', background: '#fff', color: '#444', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  uploadBtn: { padding: '9px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  uploadedNote: { color: '#22c55e', fontSize: '13px', marginTop: '10px', fontWeight: '600' },
  dashHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' },
  dashTitle: { color: '#1a3a5c', fontSize: '28px', fontWeight: 'bold', margin: '0 0 6px' },
  dashSubtitle: { color: '#4a5568', fontSize: '14px', margin: 0 },
  dashDate: { background: '#fff', padding: '10px 18px', borderRadius: '10px', color: '#555', fontSize: '13px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' },
  sectionHeading: { color: '#1a3a5c', fontSize: '18px', fontWeight: 'bold', margin: '32px 0 16px' },
  quickActions: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' },
  quickActionBtn: { background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', cursor: 'pointer', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'transform 0.2s', borderTop: '3px solid #c41e3a' },
  quickActionIcon: { fontSize: '28px', display: 'block', marginBottom: '10px' },
  quickActionLabel: { fontSize: '14px', fontWeight: 'bold', color: '#1a3a5c' },
  
  infoCard: { background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' },
  infoCardTitle: { color: '#1a3a5c', fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' },
  infoList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  infoListItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  infoListLabel: { color: '#4a5568', fontSize: '14px' },
  infoListValue: { color: '#1a3a5c', fontSize: '14px', fontWeight: '600' },
  alertBadge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', background: '#fff5f5', color: '#c41e3a', border: '1px solid #ffd0d3' },
  storeStatusText: { color: '#aaa', fontSize: '14px', marginBottom: '16px' },
  storeStatusDot: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' },
  statusDotGreen: { width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px #22c55e' },
  statusDotLabel: { color: '#aaa', fontSize: '13px' },
  viewSiteBtn: { width: '100%', padding: '10px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },


  optionalTag: { color: '#aaa', fontSize: '11px', fontWeight: 'normal' },
  inputWithPrefix: { position: 'relative' },
  inputPrefix: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666', fontSize: '14px', zIndex: 1 },
  tagInputBox: { marginBottom: '10px' },
  tagInput: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f5f7fa', color: '#333', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  tagHint: { color: '#aaa', fontSize: '12px', margin: '6px 0 0' },
  tagsDisplay: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' },
  tagChip: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1a3a5c', color: '#fff', padding: '4px 10px', borderRadius: '14px', fontSize: '13px' },
  tagRemove: { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0, opacity: 0.7 },
  quickVehicles: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: '8px' },
  quickLabel: { color: '#888', fontSize: '12px' },
  quickVehicleBtn: { padding: '4px 12px', borderRadius: '14px', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '12px', fontWeight: '500', transition: 'all 0.15s' },


  productNameCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  productThumb: { width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0', flexShrink: 0 },
  tagsCount: { background: '#f0f3f8', color: '#2d5a7b', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
};

export default AdminPage;
